<template>
    <div class="bg-white shadow-card pt-5 pb-7 pl-7 pr-8 rounded font-medium w-full relative">
        <div class="inline-flex items-center mb-7">
            <inertia-link :href="route('trade.index')">
                <i class="fas text-xs fa-arrow-left mr-3 cursor-pointer" />
            </inertia-link>
            <h4>{{ priceBoxTitle }}</h4>
        </div>
        <div class="flex items-center flex-wrap justify-between"
            :class="isChangePerspective ? 'flex-row-reverse' : 'flex-row'"
        >
            <div class="flex-1 sm:w-auto max-w-md">
                <jet-label class="sm:text-xs">Valor de {{ isBuying ? 'compra' : 'venda' }}</jet-label>
                <div class="relative flex items-center">
                    <div class="flex items-center h-6 border-r-2 border-gray-300 absolute top-1/3 pr-3 pl-3">
                        <icons :icon="'BRL'" :otherClass="'w-5'" :color="''"/>
                        <small class="text-xs ml-2">BRL</small>
                    </div>
                    <jet-decimal
                        id="amount"
                        type="text"
                        maxlength="40"
                        ref="inputAmount"
                        class="mt-1 block w-full"
                        :class="isChangePerspective ? 'bg-gray-200' : ''"
                        :value="amountField"
                        :readonly="isChangePerspective"
                        @input="updateInput('amountField', $event, !isChangePerspective)"
                    />
                </div>
            </div>
            <div class="sm:w-auto w-full inline-flex justify-center">
                <button
                    class="bg-gray-300 rounded-full w-9 h-9 relative top-2 mx-6"
                    v-tippy
                    content="Trocar perspectiva"
                    @click="$emit('change-perspective')"
                >
                    <i class="fas fa-exchange-alt" />
                </button>
            </div>
            <div class="flex-1 sm:w-auto max-w-md">
                <jet-label class="sm:text-xs">Quantidade</jet-label>
                <div class="relative flex items-center">
                    <jet-dropdown class="absolute top-1/3" :width="'24'">
                        <template slot="trigger" slot-scope="{ open }">
                            <div class="flex items-center h-6 border-r-2 border-gray-300 pr-1 pl-4 cursor-pointer">
                                <i :class="`${selectedCrypto.icon} mr-1 text-lg`" :style="{ color: selectedCrypto.color }"/>
                                <small class="text-xs mx-1">{{ selectedCrypto.value }}</small>
                                <arrow-dropdown :open="open" />
                            </div>
                        </template>
                        <template #content>
                            <jet-dropdown-link
                                v-for="crypto in optionsCrypto"
                                :key="crypto.value"
                                :as="'button'"
                                @click.native="changeCrypto(crypto)"
                            >
                                <div class="flex items-center">
                                    <icons
                                        :icon="crypto.icon"
                                        :color="crypto.color"
                                        :otherClass="'mr-1 sm:text-lg'"
                                        size="1"
                                        class="inline"
                                    />
                                    <small class="text-xs">{{ crypto.value }}</small>
                                </div>
                            </jet-dropdown-link>
                        </template>
                    </jet-dropdown>
                    <jet-decimal
                        id="quantity"
                        type="text"
                        maxlength="40"
                        ref="inputQuantity"
                        class="mt-1 block w-full"
                        :class="!isChangePerspective ? 'bg-gray-200' : ''"
                        :precision="decimal"
                        :value="quantityField"
                        :readonly="!isChangePerspective"
                        @input="updateInput('quantityField', $event, isChangePerspective)"
                    />
                </div>
            </div>
        </div>
        <div v-if="quoteError" class="text-xs text-red-600 text-right pt-2"> * {{quoteError}} </div>
        <div class="mt-4">
            <div class="flex items-center justify-between">
                <span
                    style="background-color: #FF004E"
                    class="bg-red-600 rounded text-white text-xs px-5 py-3 inline-block"
                >
                    Atenção, esta cotação expira em <b class="text-base ml-2">{{ timer }}s</b>
                </span>

                <div class="text-sm text-right">
                    <div class="text-red-500" v-if="isBuying && quantityWithoutDots < minLotSize">
                        O valor informado deve ser maior ou igual a ${{ numberFormat(minLotSize) }}
                    </div>
                    <div>Valor unitário de <b class="uppercase text-lg">{{ perspectiveValues.unitaryValue }}</b></div>
                    <span>Valor efetivo total (VET)</span>
                </div>
            </div>
            <div class="flex justify-center mt-6">
                <button
                    class="px-6 py-3 border border-transparent rounded-md
                            font-semibold text-sm text-white tracking-widest focus:outline-none
                            focus:shadow-outline-gray transition ease-in-out duration-150 w-72"
                    :class="processingOrder || (isBuying && quantityWithoutDots < minLotSize)
                        ? 'pointer-events-none cursor-not-allowed bg-gray-300'
                        : 'bg-gray-800 active:bg-gray-900 hover:bg-gray-700 focus:border-gray-900'"
                    @click="sendOrder"
                >
                    <div class="text-center">
                        <i v-show="processingOrder" class="mr-2 fas fa-spinner animate-spin" />
                        Efetivar Ordem
                    </div>
                </button>
            </div>
        </div>
        <div class="bg-gray-100 flex items-center rounded py-4 px-3 mt-4">
            <i class="fas fa-info-circle mr-2" />
            <p class="text-xs text-gray-600">Serviço de compra de cripto disponível somente das 9:05h as 16:33h.</p>
        </div>
        <div v-show="showLoader" class="absolute w-full h-full bg-gray-100 left-0 top-0 opacity-40 flex justify-center items-center z-50">
            <i  class="mr-2 fas fa-spinner animate-spin text-4xl" />
        </div>
    </div>
</template>

<script>
import JetInput from '../../../Jetstream/Input.vue';
import JetLabel from '../../../Jetstream/Label.vue';
import JetDecimal from '../../../Jetstream/Decimal.vue';
import JetDropdown from '../../../Jetstream/Dropdown.vue';
import JetDropdownLink from '../../../Jetstream/DropdownLink.vue';
import ArrowDropdown from '../../../Helpers/ArrowDropdown.vue';
import ItemValue from './ItemValue.vue';
import numberFormat from '../utils/numberFormat';
import icons from '../utils/icons'
import maskWithCommasAndDots from '../utils/maskInputWithCommaAndDots'
import Icons from '../components/Icons.vue'
import { debounce } from 'lodash';

const cryptoDefault = {
    value: 'USDT',
    decimal: 4,
    icon: "cc USDT-alt",
    color: "#50AF95"
}

export default {
    props: {
        sendOrder:{ type: Function, required:true},
        priceBoxTitle: { type: String, required: true },
        isBuying: { type: Boolean, required: true },
        processingOrder: { type: Boolean, required: true },
        isChangePerspective: { type: Boolean, default: false },
    },
    data(){
        return {
            icons,
            quantityField: '1',
            amountField: '1,00',
            timer: 5,
            selectedCrypto: { ...cryptoDefault },
            currencyValue: 0,
            precision: 4,
            decimal: 4,
            quoteError: null,
            minLotSize: 0,
            numberFormat,
            maskWithCommasAndDots,
            debounce,
            interval: null,
            showLoader: false,
            currencies: []
        }
    },
    computed: {
        quantityWithoutDots() {
            return Number(`${this.quantityField}`.replaceAll('.', '').replace(',', '.'))
        },
        optionsCrypto(){
            return this.currencies.filter(currency => currency.status === 'active')
            .map(currency => ({ ...icons[currency.coin], ...currency, value: currency.coin }))
        },
        perspectiveValues(){
            let label = this.isChangePerspective ? 'Valor de compra da cripto': 'Quantidade a ser comprada'
            if (!this.isBuying)
                label = this.isChangePerspective ? 'Valor de venda da cripto': 'Quantidade a ser vendida'

            return {
                label,
                icon: this.isChangePerspective ? 'BRL' : this.selectedCrypto.icon ,
                value: this.isChangePerspective ? `${this.amountField} BRL` : `${this.quantityField} ${this.selectedCrypto.value}`,
                unitaryValue: `
                    1,${''.padEnd(this.getDecimal(this.selectedCrypto.value), 0)} ${this.selectedCrypto.value} = ${this.currencyValue} BRL
                `
            }
        },
    },
    methods: {
        replaceText(value = '', target, newValue){
            return value.replace(target, newValue)
        },
        updateInput(field, value, searchQuote) {
            const hasNewValue = this[field] !== value
            this.input(field, value)

            if (searchQuote && hasNewValue) {
                this.cleanInput()
                this.getQuoteDebounce()
                this.clearTimer()
                this.timeToExpire()
            }
        },
        changeCrypto(crypto){
            this.selectedCrypto = crypto;
            this.decimal = this.selectedCrypto.decimal;
            this.cleanInput()
            this.getQuote()
            this.clearTimer()
            this.timeToExpire()
        },
        getDecimal(crypto){
            switch (crypto.toUpperCase()) {
                case 'ETH':
                    return 8
                case 'BTC':
                    return 8
                case 'USDT':
                    return 4
                case 'USDC':
                    return 4
                default:
                    return 2
            }
        },
        input(field, value){
            const refAmount = this.$refs.inputAmount
            const refQuantity = this.$refs.inputQuantity
            if (refAmount && refQuantity) {
                this.$refs.inputAmount.$el.value = this.replaceText(refAmount.$el.value, '-', '')
                this.$refs.inputQuantity.$el.value = this.replaceText(refQuantity.$el.value, '-', '')
            }
            this[field] = this.replaceText(value, '-', '')
        },
        timeToExpire(){
            clearInterval(this.interval)
            this.interval = setInterval(() => {
                if(this.processingOrder) return
                this.timer -= 1
                if(this.timer === -1) {
                    this.getQuote()
                    this.clearTimer()
                    this.timeToExpire()
                }
            }, 1000)
        },
        cleanInput(){
            if (this.isChangePerspective) {
                this.amountField = 0
            } else {
                this.quantityField = 0
            }
        },
        getQuoteDebounce: debounce(async function() { this.getQuote() }, 500),
        async getQuote() {
            const amount = (
                this.isChangePerspective ?
                    Number(this.quantityField.replaceAll('.', '').replace(',', '.')) :
                    Number(this.amountField.replaceAll('.', '').replace(',', '.'))
            )

            let params = {
                coin: this.selectedCrypto.value,
                settlement: 'BRL',
                amount: amount,
                is_fiat: !this.isChangePerspective,
                operation: this.isBuying ? 'buy' : 'sell',
            }
            this.showLoader = true
            await axios.get('/crypto/quote', { params: params })
                .then(response => {
                    if (response.data.error_code) {
                        this.currencyValue = 0
                        this.cleanInput()

                        this.quoteError = response.data.message
                        this.showLoader = false
                        this.$emit('att-form', {
                            coin: this.selectedCrypto.value,
                            settlement: 'BRL',
                            amount: 0,
                            total: 0,
                            unit_value: 0,
                            quote_error: true
                        });
                        return
                    }

                    this.currencyValue = numberFormat(response.data.unit_value.toString(), 'USDT');

                    if (this.isChangePerspective) {
                        this.amountField = numberFormat(response.data.total.toString(), response.data.settlement);
                    } else {
                        this.quantityField = numberFormat(response.data.amount.toString(), response.data.coin);
                    }
                    this.minLotSize = response.data.min_lot_size
                    this.quoteError = null;
                    this.showLoader = false
                    this.$emit('att-form', response.data);
                });
        },
        clearTimer() {
            clearInterval(this.interval)
            this.timer = 5
        }
    },
    mounted() {
        this.timeToExpire()
    },
    async created(){
        const { data = {} } = await axios.get('/crypto/currencies')
        this.currencies = data.list ?? []
        this.getQuote()
    },
    destroyed(){
        clearInterval(this.interval)
    },
    components: {
        JetInput,
        JetLabel,
        JetDecimal,
        JetDropdown,
        JetDropdownLink,
        ArrowDropdown,
        ItemValue,
        Icons
    },
}
</script>
